/**
 * Conteúdo do website institucional do Vende Aí
 *
 * Textos, funcionalidades, segmentos, perguntas frequentes e planos de
 * contingência (exibidos se a API de planos estiver indisponível).
 * As funcionalidades listadas correspondem a módulos existentes no sistema.
 *
 * @author Equipe VendeAi
 * @since 1.1.0
 */

import type { LucideIcon } from 'lucide-react';
import {
  Smartphone,
  QrCode,
  CreditCard,
  MessageCircle,
  BarChart3,
  Pizza,
  Store,
  Bike,
  Tv,
  Package,
  Wallet,
  FileText,
  Users,
  Gift,
  CalendarClock,
  Bell,
  Download,
  ShieldCheck,
  Repeat,
  Landmark,
  Undo2,
  MessageSquareWarning,
  Percent,
  ClipboardX,
  Receipt,
  UserX,
  TrendingDown,
  NotebookPen,
  Hourglass,
} from 'lucide-react';
import type { SubscriptionPlan } from '@services/platformService';
import { SEGMENT_PAGES } from './segments';

export const BRAND = 'Vende Aí';

// =========================================================================
// Funcionalidades por jornada (abas)
// =========================================================================

export interface FeatureItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface FeatureGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  headline: string;
  description: string;
  items: FeatureItem[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    key: 'delivery',
    label: 'Delivery',
    icon: Smartphone,
    headline: 'Seu próprio canal de vendas, sem comissão por pedido',
    description:
      'Uma loja online com a cara do seu negócio. Seu cliente pede pelo link ou pelo Instagram, paga pelo celular e acompanha o pedido em tempo real.',
    items: [
      {
        icon: Store,
        title: 'Cardápio digital com link próprio',
        description: 'Loja online personalizada com logo, capa, cores, banners e promoções.',
      },
      {
        icon: Pizza,
        title: 'Pizza meio a meio, tamanhos e adicionais',
        description:
          'Sabores fracionados, bordas, complementos obrigatórios e opcionais, preço por tamanho.',
      },
      {
        icon: Bike,
        title: 'Entregas e entregadores',
        description: 'Taxas por região, atribuição de entregadores e acompanhamento da rota.',
      },
      {
        icon: CalendarClock,
        title: 'Encomendas e horários marcados',
        description:
          'Pedidos para outro dia e horário, com limite por dia e pela escala da equipe. Serve para bolos, marmitas e serviços.',
      },
    ],
  },
  {
    key: 'salao',
    label: 'Salão e mesas',
    icon: QrCode,
    headline: 'O cliente pede e paga pela mesa, sem esperar o garçom',
    description:
      'Cada mesa ganha um QR Code. O cliente abre o cardápio, faz o pedido, a cozinha recebe na hora e a conta pode ser paga pelo celular, inclusive dividida.',
    items: [
      {
        icon: QrCode,
        title: 'QR Code em cada mesa',
        description: 'Cardápio aberto no celular do cliente, sem baixar aplicativo.',
      },
      {
        icon: Wallet,
        title: 'Conta paga pelo celular',
        description: 'PIX direto da mesa, com pagamento parcial e liberação automática da mesa.',
      },
      {
        icon: Tv,
        title: 'Painel de pedidos para TV',
        description: 'Tela de fila para cozinha e balcão, com status de cada pedido em tempo real.',
      },
      {
        icon: Bell,
        title: 'Alertas sonoros e notificações',
        description:
          'Nenhum pedido passa despercebido: aviso sonoro, push e atualização instantânea.',
      },
    ],
  },
  {
    key: 'pagamentos',
    label: 'Pagamentos',
    icon: CreditCard,
    headline: 'PIX e cartão direto na sua conta, sem golpe do comprovante',
    description:
      'Conecte o seu banco ou gateway. O sistema confirma o PIX consultando o próprio banco, baixa o pedido sozinho e o dinheiro cai direto na sua conta. O Vende Aí não intermedia e não cobra comissão. Na maquininha integrada (Mercado Pago Point, Cielo Smart, Stone e SumUp), a cobrança vai do pedido direto para o terminal, sem digitar o valor.',
    items: [
      {
        icon: ShieldCheck,
        title: 'PIX confirmado no banco',
        description:
          'QR Code e copia e cola no pedido. A confirmação vem do banco, não de um print enviado pelo cliente.',
      },
      {
        icon: CreditCard,
        title: 'Cartão com pré-autorização',
        description:
          'No Mercado Pago, PagBank, Pagar.me, Cielo, Stripe, Zoop, Adyen e PicPay, o valor fica reservado e só é cobrado quando você aceita o pedido. Se recusar, a reserva é liberada.',
      },
      {
        icon: Landmark,
        title: 'Seu banco ou gateway',
        description:
          'Mercado Pago, PagBank, Asaas, Pagar.me, Cielo, Stone, Rede, Getnet, InfinitePay, SumUp, Stripe, Iugu, Vindi, Zoop, Adyen, Tuna, PicPay, PayPal, NuPay, Woovi/OpenPix, PagHiper, Safe2Pay, iPag, Cora, Banco Inter e API Pix de Itaú, Bradesco, Banco do Brasil, Santander, Sicoob, Sicredi, Efí e C6.',
      },
      {
        icon: Undo2,
        title: 'Estorno automático',
        description:
          'Cancelou um pedido já pago? O sistema devolve o PIX pelo próprio banco nos provedores que oferecem estorno.',
      },
    ],
  },
  {
    key: 'marketing',
    label: 'WhatsApp e marketing',
    icon: MessageCircle,
    headline: 'Um robô que atende e fecha o pedido no WhatsApp, até no pico',
    description:
      'Conecte o WhatsApp do seu negócio e deixe o robô atender: ele mostra o cardápio, monta o pedido e avisa cada etapa. As campanhas automáticas trazem o cliente de volta.',
    items: [
      {
        icon: MessageCircle,
        title: 'Pedido completo pela conversa',
        description:
          'Cardápio, carrinho, cupom, entrega ou retirada, agendamento e forma de pagamento, tudo por opções numeradas.',
      },
      {
        icon: Bell,
        title: 'Avisos automáticos de status',
        description:
          'Confirmado, em preparo, saiu para entrega, pronto para retirar ou reagendado.',
      },
      {
        icon: Repeat,
        title: 'Campanhas que trazem o cliente de volta',
        description:
          'Aniversário, boas-vindas, pós-venda, promoção relâmpago e reativação de clientes inativos.',
      },
      {
        icon: Gift,
        title: 'Fidelidade, cashback e carrinho abandonado',
        description:
          'Pontos, recompensas, cupons e recuperação de quem desistiu no meio do pedido.',
      },
    ],
  },
  {
    key: 'gestao',
    label: 'Gestão',
    icon: BarChart3,
    headline: 'Estoque, financeiro e fiscal no mesmo lugar',
    description:
      'Muito além do cardápio: controle o negócio inteiro com relatórios em tempo real, estoque por ingrediente, contas a pagar e emissão de notas.',
    items: [
      {
        icon: Package,
        title: 'Estoque e ficha técnica',
        description: 'Baixa automática por ingrediente a cada venda e alertas de estoque baixo.',
      },
      {
        icon: BarChart3,
        title: 'Financeiro e relatórios',
        description: 'Receitas, despesas, contas bancárias, conciliação OFX e indicadores.',
      },
      {
        icon: FileText,
        title: 'Nota fiscal',
        description: 'Emissão de NF-e e NFC-e integrada à SEFAZ.',
      },
      {
        icon: Users,
        title: 'Equipe e permissões',
        description: 'Usuários por função, RH, escalas e ponto eletrônico.',
      },
    ],
  },
];

// =========================================================================
// Segmentos (mapeados para TenantType do backend; detalhes em segments.ts)
// =========================================================================

export const SEGMENTS = SEGMENT_PAGES.map(
  ({ type, slug, label, icon, description, highlights }) => ({
    type,
    slug,
    label,
    icon,
    description,
    highlights,
  }),
);

export type Segment = (typeof SEGMENTS)[number];

// =========================================================================
// Dores do dono do negócio (seção "Você se reconhece?")
// =========================================================================

export interface PainPoint {
  icon: LucideIcon;
  pain: string;
  description: string;
  solution: string;
}

export const PAIN_POINTS: PainPoint[] = [
  {
    icon: MessageSquareWarning,
    pain: 'WhatsApp lotado na hora do pico',
    description:
      'Cliente esperando resposta, áudio para ouvir e pedido que se perde no meio da conversa.',
    solution: 'Robô que monta o pedido completo sozinho, sem atendente.',
  },
  {
    icon: Percent,
    pain: 'Comissão de marketplace comendo a margem',
    description: 'De 12% a 27% de cada venda fica com o aplicativo, e o cliente nem é seu.',
    solution: 'Canal próprio, mensalidade fixa e pedidos ilimitados.',
  },
  {
    icon: ClipboardX,
    pain: 'Pedido anotado errado',
    description:
      'Sabor trocado, adicional esquecido, endereço incompleto. Retrabalho, prejuízo e cliente irritado.',
    solution: 'O cliente monta o pedido e ele chega completo na cozinha.',
  },
  {
    icon: Receipt,
    pain: 'Comprovante de PIX que não confere',
    description: 'Conferir extrato no meio do movimento e ainda correr o risco de golpe.',
    solution: 'PIX confirmado direto no seu banco, sem depender de print.',
  },
  {
    icon: Hourglass,
    pain: 'Cliente esperando para pedir e pagar',
    description: 'Mesa parada esperando o garçom e fila no balcão perguntando pelo pedido.',
    solution: 'QR Code na mesa e painel de fila na TV.',
  },
  {
    icon: TrendingDown,
    pain: 'Não saber se está dando lucro',
    description: 'Estoque controlado de cabeça, insumo acabando sem aviso e caixa no caderno.',
    solution: 'Ficha técnica, baixa automática de estoque e financeiro.',
  },
  {
    icon: UserX,
    pain: 'Cliente que compra uma vez e some',
    description: 'Sem cadastro, sem histórico e sem jeito de chamar de volta.',
    solution: 'Fidelidade, cashback e campanhas automáticas.',
  },
  {
    icon: NotebookPen,
    pain: 'Agenda e encomendas no caderno',
    description: 'Bolo esquecido, horário marcado duas vezes e cliente ligando só para perguntar.',
    solution: 'Agendamento online com limite de horários.',
  },
];

// =========================================================================
// Implantação (passo a passo)
// =========================================================================

export const ONBOARDING_STEPS = [
  {
    title: 'Crie sua conta',
    description: 'Preencha um formulário rápido, sem cartão de crédito. Leva cerca de 2 minutos.',
    time: '2 min',
  },
  {
    title: 'Sua loja nasce pronta',
    description:
      'Escolha o segmento e já recebe a loja online com categorias iniciais e link próprio.',
    time: 'Na hora',
  },
  {
    title: 'Monte seu cardápio',
    description: 'Cadastre os produtos, importe por planilha ou traga seu catálogo do iFood.',
    time: '30 min',
  },
  {
    title: 'Comece a vender',
    description: 'Divulgue o link, imprima os QR Codes das mesas e conecte o pagamento online.',
    time: 'Mesmo dia',
  },
];

// =========================================================================
// Antes x Depois
// =========================================================================

export const BEFORE_AFTER = {
  before: [
    'WhatsApp lotado e pedidos anotados no papel, com erros e esquecimentos',
    'Até 27% de cada venda indo para comissão de marketplace',
    'Comprovante de PIX conferido na mão, com risco de golpe',
    'Cliente esperando o garçom para pedir e para pagar',
    'Estoque e caixa conferidos na mão no fim do dia',
    'Nenhum dado sobre quem são seus melhores clientes',
  ],
  after: [
    'Robô que fecha pedidos e todos os pedidos em um painel único',
    'Mensalidade fixa e zero comissão por pedido',
    'PIX confirmado direto no banco e baixa automática do pedido',
    'Cliente pede e paga pelo QR Code da mesa',
    'Estoque baixado automaticamente e financeiro organizado',
    'Base de clientes própria com fidelidade e campanhas',
  ],
};

// =========================================================================
// Comparativo (sem citar marcas)
// =========================================================================

export const COMPARISON_ROWS: {
  label: string;
  vendeai: string | boolean;
  marketplace: string | boolean;
  others: string | boolean;
}[] = [
  {
    label: 'Comissão sobre cada pedido',
    vendeai: 'Zero',
    marketplace: '12% a 27%',
    others: 'Zero ou por faixa de pedidos',
  },
  { label: 'Pedidos ilimitados', vendeai: true, marketplace: true, others: 'Depende do plano' },
  {
    label: 'Robô de WhatsApp que fecha pedidos',
    vendeai: true,
    marketplace: false,
    others: 'Em alguns planos',
  },
  {
    label: 'PIX direto na sua conta',
    vendeai: true,
    marketplace: 'Repasse depois',
    others: 'Varia',
  },
  { label: 'Clientes e dados são seus', vendeai: true, marketplace: false, others: true },
  {
    label: 'Delivery + QR Code na mesa',
    vendeai: true,
    marketplace: false,
    others: 'Plano superior',
  },
  {
    label: 'Estoque, financeiro e nota fiscal',
    vendeai: true,
    marketplace: false,
    others: 'Módulos pagos à parte',
  },
  { label: 'Teste grátis', vendeai: '30 dias', marketplace: false, others: '7 dias, em média' },
  { label: 'Fidelidade contratual', vendeai: 'Não', marketplace: 'Não', others: 'Às vezes' },
];

// =========================================================================
// Perguntas frequentes
// =========================================================================

export const FAQ_ITEMS = [
  {
    question: 'Como funciona o teste grátis de 30 dias?',
    answer:
      'Você cria sua conta sem cartão de crédito e usa o sistema completo por 30 dias. Ao final, escolhe o plano que faz sentido para o seu negócio. Se não quiser continuar, basta não contratar: não há cobrança automática.',
  },
  {
    question: 'Vocês cobram taxa ou comissão por pedido?',
    answer:
      'Não. O Vende Aí cobra apenas a mensalidade fixa do plano, com pedidos ilimitados. Quando o cliente paga online (PIX ou cartão), incide somente a taxa do banco ou gateway que você escolher, cobrada por ele, não por nós.',
  },
  {
    question: 'Quando o dinheiro das vendas online cai na minha conta?',
    answer:
      'Os pagamentos caem direto na conta do banco ou gateway que você conectar: Mercado Pago, PagBank, Asaas, Pagar.me, Cielo, Stripe, Banco Inter ou outro banco e gateway integrado. No PIX o valor fica disponível na hora; no cartão, segue o prazo do seu gateway.',
  },
  {
    question: 'Como o robô do WhatsApp funciona?',
    answer:
      'Você conecta o WhatsApp do seu negócio lendo um QR Code, como no WhatsApp Web. O robô responde os clientes, mostra o cardápio e monta o pedido por opções numeradas, com cupom, entrega ou retirada, agendamento e forma de pagamento. O pedido cai no painel como qualquer outro, e o cliente recebe os avisos de status.',
  },
  {
    question: 'Funciona para barbearia e negócios que não são de comida?',
    answer:
      'Sim. A loja online vende serviços e produtos. Na barbearia, o cliente escolhe o serviço, o dia e um horário livre, calculado pela escala e pelas especialidades de cada profissional, e recebe a confirmação pelo WhatsApp.',
  },
  {
    question: 'Existe fidelidade ou multa para cancelar?',
    answer:
      'Não. O plano mensal pode ser cancelado quando você quiser. O plano anual oferece desconto para quem prefere pagar o ano todo.',
  },
  {
    question: 'Como funciona a implantação?',
    answer:
      'A implantação é gratuita e self-service: ao se cadastrar, sua loja já é criada com as categorias do seu segmento. Você pode cadastrar produtos manualmente, importar por planilha Excel ou trazer o catálogo do iFood. Se preferir, nosso time ajuda na configuração.',
  },
  {
    question: 'Preciso instalar algum programa ou comprar equipamento?',
    answer:
      'Não. O Vende Aí funciona no navegador do computador, tablet ou celular, e pode ser instalado como aplicativo (PWA). Para imprimir pedidos, use qualquer impressora térmica ou comum.',
  },
  {
    question: 'Qual a diferença entre o Vende Aí e o iFood? Posso usar os dois?',
    answer:
      'O iFood é um marketplace: o cliente encontra várias lojas no aplicativo dele e você paga comissão sobre cada pedido. O Vende Aí é o seu canal próprio: link da loja, robô de WhatsApp e QR Code na mesa, com mensalidade fixa, sem comissão, e os dados dos clientes ficam com você. Dá para usar os dois: muitos clientes usam o marketplace para ser encontrados e o Vende Aí para vender direto e fidelizar. Você ainda pode importar o cardápio do iFood para começar mais rápido.',
  },
  {
    question: 'Quanto custa o Vende Aí?',
    answer:
      'Você começa com 30 dias grátis, sem cartão de crédito. Depois, paga uma mensalidade fixa conforme o plano (Delivery, Completo ou Pro), com pedidos ilimitados e sem comissão por pedido. O plano anual tem desconto. Os valores atualizados estão na seção de planos desta página.',
  },
  {
    question: 'O Vende Aí emite nota fiscal?',
    answer:
      'Sim. Nos planos Completo e Pro, o sistema emite NF-e e NFC-e integradas à SEFAZ, usando o certificado digital A1 do seu CNPJ.',
  },
  {
    question: 'Meus dados e os dos meus clientes estão seguros?',
    answer:
      'Sim. Os dados de cada estabelecimento são isolados, as senhas são criptografadas e o sistema segue a LGPD, com registro de consentimento, exportação e exclusão de dados.',
  },
];

// =========================================================================
// Planos de contingência (usados apenas se a API estiver indisponível)
// =========================================================================

const fallback = (
  p: Partial<SubscriptionPlan> &
    Pick<SubscriptionPlan, 'name' | 'slug' | 'priceMonthly' | 'features'>,
): SubscriptionPlan => ({
  id: p.slug,
  description: null,
  priceYearly: null,
  setupFee: 0,
  trialDays: 30,
  isTrial: false,
  limits: null,
  highlight: false,
  badge: null,
  isPublic: true,
  isActive: true,
  sortOrder: 0,
  ...p,
});

export const FALLBACK_PLANS: SubscriptionPlan[] = [
  fallback({
    name: 'Teste Grátis',
    slug: 'teste-gratis',
    isTrial: true,
    priceMonthly: 0,
    description: 'Use o sistema completo por 30 dias, sem cartão de crédito e sem compromisso.',
    features: [
      'Todos os recursos liberados por 30 dias',
      'Cardápio digital com link e QR Code',
      'Pedidos ilimitados',
      'PIX e cartão online direto na sua conta',
      'Sem cartão de crédito',
    ],
  }),
  fallback({
    name: 'Delivery',
    slug: 'delivery',
    priceMonthly: 99.9,
    priceYearly: 958.8,
    description: 'Para quem quer vender pelo próprio canal, sem comissão por pedido.',
    features: [
      'Cardápio digital com link próprio',
      'Pedidos ilimitados e sem comissão',
      'PIX e cartão online direto na sua conta',
      'Painel de pedidos em tempo real',
      'Gestão de entregadores',
      'Cupons de desconto',
      'Até 3 usuários',
    ],
  }),
  fallback({
    name: 'Completo',
    slug: 'completo',
    priceMonthly: 169.9,
    priceYearly: 1678.8,
    highlight: true,
    badge: 'Mais escolhido',
    description: 'Delivery + salão: QR Code na mesa, estoque e financeiro no mesmo plano.',
    features: [
      'Tudo do plano Delivery',
      'QR Code na mesa e gestão de mesas',
      'Pagamento da conta pelo celular',
      'Automação de WhatsApp',
      'Estoque e ficha técnica',
      'Financeiro e relatórios',
      'Fidelidade e marketing',
      'Nota fiscal (NF-e/NFC-e)',
      'Até 10 usuários',
    ],
  }),
  fallback({
    name: 'Pro',
    slug: 'pro',
    priceMonthly: 279.9,
    priceYearly: 2758.8,
    description: 'Para operações maiores que precisam de tudo, com atendimento prioritário.',
    features: [
      'Tudo do plano Completo',
      'Usuários ilimitados',
      'RH, ponto eletrônico e escalas',
      'Conciliação bancária (OFX)',
      'Recuperação de carrinho abandonado',
      'Suporte prioritário',
      'Implantação assistida',
    ],
  }),
];

export const INTEGRATIONS = [
  { label: 'PIX direto no seu banco', icon: QrCode },
  { label: 'Mercado Pago', icon: CreditCard },
  { label: 'Asaas', icon: CreditCard },
  { label: 'PagBank', icon: CreditCard },
  { label: 'Pagar.me', icon: CreditCard },
  { label: 'Cielo', icon: CreditCard },
  { label: 'Stripe', icon: CreditCard },
  { label: 'Stone, Rede e Getnet', icon: CreditCard },
  { label: 'InfinitePay e SumUp', icon: CreditCard },
  { label: 'Iugu, Vindi, Zoop e Adyen', icon: CreditCard },
  { label: 'PicPay, PayPal e NuPay', icon: CreditCard },
  { label: 'Tuna, Woovi, PagHiper, Safe2Pay e iPag', icon: CreditCard },
  { label: 'Maquininha integrada (Point, Cielo, Stone, SumUp)', icon: CreditCard },
  { label: 'Cora', icon: Landmark },
  { label: 'Banco Inter', icon: Landmark },
  { label: 'Itaú, Bradesco, BB e mais bancos', icon: Landmark },
  { label: 'WhatsApp', icon: MessageCircle },
  { label: 'Importação do iFood', icon: Download },
  { label: 'NF-e / NFC-e (SEFAZ)', icon: FileText },
  { label: 'App instalável (PWA)', icon: Smartphone },
];
